import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SportsSoccerIcon from "@mui/icons-material/SportsSoccer";
import { Box, Button, Divider, Grid, Stack, Tooltip, Typography, } from "@mui/material";
import { useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { useRetrievePlayerByUserId, useRetrieveTeamsByPlayerId, } from "../ApiProvider";
import { CountChip } from "../utils/CountChip";
import { UserContext } from "../auth/UserProvider";
import { EmptyState } from "../common/EmptyState";
import { ErrorView } from "../nav/Error";
import { Loading } from "../nav/Loading";
import { TeamCard } from "./TeamCard";
const TeamSection = ({ title, icon, teams, tooltip }) => (_jsxs(Box, { children: [_jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", sx: { mb: 2 }, children: [icon, _jsx(Typography, { variant: "h6", children: title }), _jsx(CountChip, { count: teams.length }), tooltip && (_jsx(Tooltip, { title: tooltip, children: _jsx(InfoOutlinedIcon, { sx: { fontSize: "1.1rem", color: "text.secondary" } }) }))] }), _jsx(Grid, { container: true, spacing: 2, children: teams.map((team) => (_jsx(Grid, { item: true, xs: 12, sm: 6, md: 4, children: _jsx(TeamCard, { team: team }) }, team.id))) })] }));
const Teams = () => {
    const { user: contextUser, loading: isContextUserLoading } = useContext(UserContext);
    const { isLoading: isPlayerLoading, error: playerError, data: player, } = useRetrievePlayerByUserId({ userId: contextUser?.pk });
    const { isLoading, error, data: teams, } = useRetrieveTeamsByPlayerId({
        playerId: player?.id,
    });
    const currentUserId = contextUser?.pk;
    const [managerTeams, playerTeams, otherTeams] = useMemo(() => {
        if (!teams)
            return [[], [], []];
        const managed = [];
        const playerList = [];
        const other = [];
        for (const team of [...teams].sort((a, b) => a.name.localeCompare(b.name))) {
            if (team.manager === currentUserId) {
                managed.push(team);
            }
            else if (player?.id && team.players?.includes(player.id)) {
                playerList.push(team);
            }
            else {
                other.push(team);
            }
        }
        return [managed, playerList, other];
    }, [teams, currentUserId, player?.id]);
    if (isLoading || isPlayerLoading || isContextUserLoading) {
        return _jsx(Loading, {});
    }
    if (error || teams === undefined || playerError) {
        return (_jsx(ErrorView, { error: error || new Error("Failed to retrieve teams data.") }));
    }
    return (_jsxs(Box, { children: [_jsxs(Stack, { direction: "row", justifyContent: "space-between", alignItems: "center", children: [_jsx(Typography, { variant: "h4", children: "Teams" }), _jsx(Button, { variant: "contained", color: "primary", size: "large", component: Link, to: "/teams/create/", children: "Create Team" })] }), _jsx(Divider, { sx: { my: 2 } }), teams.length > 0 ? (_jsxs(Stack, { spacing: 4, children: [managerTeams.length > 0 && (_jsx(TeamSection, { title: "Manager", icon: _jsx(AdminPanelSettingsOutlinedIcon, { sx: { color: "text.secondary" } }), teams: managerTeams })), playerTeams.length > 0 && (_jsx(TeamSection, { title: "Player", icon: _jsx(SportsSoccerIcon, { sx: { color: "text.secondary" } }), teams: playerTeams })), otherTeams.length > 0 && (_jsx(TeamSection, { title: "Other Teams", icon: _jsx(GroupsIcon, { sx: { color: "text.secondary" } }), teams: otherTeams, tooltip: "Teams you created but don't actively manage or play in" }))] })) : (_jsx(EmptyState, { icon: _jsx(GroupsIcon, {}), heading: "No teams yet", description: "You're not part of any teams. Create one to start managing your squad, tracking stats, and organising matches.", actionLabel: "Create Team", actionTo: "/teams/create/" }))] }));
};
export default Teams;
